/**
 * Kiosk Payment Page
 *
 * 1. The order is priced by the SERVER on arrival (subtotal, promo, tax, total);
 *    the customer adds a tip from the restaurant's own presets.
 * 2. Card on this kiosk's reader (Aster Gateway): the screen polls the payment
 *    until it is final and shows only what the gateway said
 *    (docs/CHECKOUT_API_2026-10-28.md §1, §2, §4).
 * 3. The order + try are kept in localStorage: a reload resumes the poll, never
 *    a second charge. A timed-out / error try is retried with the same amount
 *    and tip (the server re-reads it first: a late approval comes back as is).
 * 4. Result screens reset the kiosk on their own; an order is abandoned only
 *    when nothing was or may still be charged (utils/kioskPath.canAbandon).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';
import { useKiosk } from '../context/KioskContext';
import { toast } from 'sonner';
import TipSelector from '../components/checkout/TipSelector';
import OrderSummary from '../components/checkout/OrderSummary';
import PaymentTotals from '../components/checkout/PaymentTotals';
import PaymentMethodSelector from '../components/payment/PaymentMethodSelector';
import PaymentProcessingScreen from '../components/payment/PaymentProcessingScreen';
import { canAbandon, kioskPath, READER_UNAVAILABLE } from '../utils/kioskPath';

const POLL_MS = 1500;
const QUIET_POLL_MS = 5000;        // a timed-out try: keep asking, a late approval shows up
const LONG_WAIT_MS = 90 * 1000;    // after this the screen says "Checking your payment…"
const RESET_AFTER_S = { declined: 30, timed_out: 60, error: 45, unavailable: 30, partial: 90 };
const BUSY = ['pricing', 'starting', 'resuming', 'waiting', 'cancelling', 'approved'];

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};
const isReaderUnavailable = (text) => /card reader unavailable/i.test(text || '');

export default function PaymentPage() {
  const {
    cart,
    getCartCount,
    promoDiscount,
    tip,
    tipPercentage,
    setTipByPercentage,
    setCustomTip,
    placeOrder,
    abandonOrder,
    startCardPayment,
    getPayment,
    cancelPayment,
    cardReady,
    config,
    refreshConfig,
    tipsEnabled,
    tipPresets,
    activePayment,
    setActivePayment,
    setPaymentPhase,
    setOrder,
    setView,
    resetSession,
  } = useKiosk();

  const navigate = useNavigate();
  const resumed = useRef(activePayment);
  const [priced, setPriced] = useState(resumed.current?.order || null);
  const [phase, setPhase] = useState(() => {
    if (resumed.current?.payment_id) return 'resuming';
    return resumed.current?.order ? 'form' : 'pricing';
  });
  const [view, setPaymentView] = useState(null);
  const [message, setMessage] = useState('');
  const [longWait, setLongWait] = useState(false);
  const [resetIn, setResetIn] = useState(null);
  const [orderSummaryExpanded, setOrderSummaryExpanded] = useState(false);
  const paymentIdRef = useRef(resumed.current?.payment_id || null);
  const waitStartedRef = useRef(Date.now());
  const pricedOnce = useRef(false);

  const tipCents = tipsEnabled ? Math.round((tip || 0) * 100) : 0;
  const subtotal = priced ? priced.subtotal_cents / 100 : 0;
  const tipBase = priced ? (priced.subtotal_cents - (priced.discount_cents || 0)) / 100 : 0;

  // the idle guard stays away while a card try is open or a result screen runs its own reset
  useEffect(() => {
    setPaymentPhase(BUSY.includes(phase) ? 'busy' : (phase === 'form' ? 'form' : 'result'));
  }, [phase, setPaymentPhase]);
  useEffect(() => () => setPaymentPhase('form'), [setPaymentPhase]);

  /* ─── 1. the server prices the order ─── */
  useEffect(() => {
    if (priced || pricedOnce.current) return;
    pricedOnce.current = true;
    if (!cart.length) { navigate(kioskPath('menu'), { replace: true }); return; }
    (async () => {
      try {
        const order = await placeOrder();
        setPriced(order);
        setActivePayment({ order, payment_id: null, status: null, tip_cents: 0, approved_cents: 0, charged_cents: 0 });
        setPhase('form');
      } catch (error) {
        setMessage(errorText(error, 'We could not price your order. Please try again or see staff.'));
        setPhase('price_error');
      }
    })();
  }, [priced, cart.length, placeOrder, navigate, setActivePayment]);

  /* ─── 2. every PaymentView the server answers ─── */
  const track = useCallback((v) => {
    if (!v) return;
    if (v.payment_id !== paymentIdRef.current) {
      paymentIdRef.current = v.payment_id;
      waitStartedRef.current = Date.now();
      setLongWait(false);
    }
    setPaymentView(v);
    const approvedNow = v.status === 'approved' ? (v.approved_cents || 0) + (v.tip_cents || 0) + (v.fee_cents || 0) : 0;
    setActivePayment((prev) => {
      const counted = prev?.counted || {};
      const add = approvedNow && !counted[v.payment_id] ? approvedNow : 0;
      return {
        ...(prev || {}), order: prev?.order || priced, payment_id: v.payment_id, status: v.status, tip_cents: v.tip_cents,
        approved_cents: (prev?.approved_cents || 0) + (add ? v.approved_cents || 0 : 0),
        charged_cents: (prev?.charged_cents || 0) + add,
        counted: add ? { ...counted, [v.payment_id]: true } : counted,
      };
    });
    if (!v.is_final) { setPhase(v.status === 'cancelling' ? 'cancelling' : 'waiting'); return; }
    if (v.status === 'approved') {
      setPhase((v.remaining_cents || 0) > 0 && v.order_payment_status !== 'paid' ? 'partial' : 'approved');
    } else if (v.status === 'cancelled') {
      toast('Payment cancelled');
      setPhase('form');
    } else {
      setPhase(['declined', 'timed_out', 'error'].includes(v.status) ? v.status : 'error');
    }
  }, [priced, setActivePayment]);

  /* ─── poll while the try is open (and quietly after a time-out: a late approval) ─── */
  useEffect(() => {
    if (!['resuming', 'waiting', 'cancelling', 'timed_out'].includes(phase) || !paymentIdRef.current) return undefined;
    let stopped = false;
    let timer;
    const quiet = phase === 'timed_out';
    const tick = async () => {
      try {
        const v = await getPayment(paymentIdRef.current);
        if (stopped) return;
        if (!quiet || v.status !== 'timed_out') track(v);
      } catch (error) {
        if (stopped) return;
        if (error?.response?.status === 404 && phase === 'resuming') {
          // the kept try is not this kiosk's any more: start clean
          resetSession();
          navigate(kioskPath(''), { replace: true });
          return;
        }
        // the server keeps the sale; keep asking
      }
      if (!stopped) timer = setTimeout(tick, quiet ? QUIET_POLL_MS : POLL_MS);
    };
    timer = setTimeout(tick, phase === 'resuming' ? 0 : (quiet ? QUIET_POLL_MS : POLL_MS));
    return () => { stopped = true; clearTimeout(timer); };
  }, [phase, getPayment, track, resetSession, navigate]);

  /* ─── long wait: never a Retry while the try is open ─── */
  useEffect(() => {
    if (phase !== 'waiting' || longWait) return undefined;
    const left = Math.max(0, LONG_WAIT_MS - (Date.now() - waitStartedRef.current));
    const timer = setTimeout(() => setLongWait(true), left);
    return () => clearTimeout(timer);
  }, [phase, longWait]);

  /* ─── approved: on to the order number ─── */
  useEffect(() => {
    if (phase !== 'approved' || !view) return undefined;
    const charged = Math.max(activePayment?.charged_cents || 0,
      (view.approved_cents || 0) + (view.tip_cents || 0) + (view.fee_cents || 0));
    const timer = setTimeout(() => {
      setOrder({
        ...(priced || {}), order_number: view.order_number || priced?.order_number, payment_approved: true,
        charged_cents: charged, fee_cents: view.fee_cents || 0, tip_cents: view.tip_cents || 0,
        card_type: view.card_brand, last_four: view.last4, estimated_time: priced?.estimated_ready_minutes,
      });
      setActivePayment(null);
      setView('confirmation');
      navigate(kioskPath('confirmation'));
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, view]);

  /* ─── leaving ─── */
  const leftRef = useRef(false);
  const startOver = useCallback(async () => {
    if (leftRef.current) return;
    leftRef.current = true;
    const active = activePayment;
    if (canAbandon(active)) await abandonOrder(active.order.order_id);
    resetSession();
    navigate(kioskPath(''));
  }, [activePayment, abandonOrder, resetSession, navigate]);

  // result screens reset the kiosk on their own
  useEffect(() => {
    const seconds = RESET_AFTER_S[phase];
    if (!seconds) { setResetIn(null); return undefined; }
    setResetIn(seconds);
    const timer = setInterval(() => setResetIn((s) => (s == null ? s : s - 1)), 1000);
    return () => clearInterval(timer);
  }, [phase]);
  useEffect(() => {
    if (resetIn === 0) startOver();
  }, [resetIn, startOver]);
  // a touch on a result screen gives the customer the full time again
  useEffect(() => {
    if (!RESET_AFTER_S[phase]) return undefined;
    const bump = () => setResetIn(RESET_AFTER_S[phase]);
    window.addEventListener('pointerdown', bump);
    return () => window.removeEventListener('pointerdown', bump);
  }, [phase]);

  const handleBack = async () => {
    if (phase !== 'form' && phase !== 'price_error') return;
    if (canAbandon(activePayment)) await abandonOrder(activePayment.order.order_id);
    setActivePayment(null);
    setView('cart');
    navigate(kioskPath('cart'));
  };

  /* ─── 3. pay ─── */
  const pay = async (tipForTry) => {
    if (!priced) return;
    setPhase('starting');
    const fresh = await refreshConfig();
    if (fresh && !fresh.payment?.card_ready) {
      const reason = (fresh.payment?.reasons || [])[0];
      setMessage(reason && !isReaderUnavailable(reason) ? reason : '');
      setPhase('unavailable');
      return;
    }
    try {
      const v = await startCardPayment(priced.order_id, tipForTry);
      if (v.is_final && v.status === 'timed_out' && v.payment_id === paymentIdRef.current) {
        toast('Still no answer from the card reader. We keep checking.');
      }
      track(v);
    } catch (error) {
      const text = errorText(error, '');
      if (error?.response?.status === 409 && isReaderUnavailable(text)) {
        setMessage('');
        setPhase('unavailable');
        return;
      }
      toast.error(text || 'The payment could not start. Please try again.');
      setPhase(activePayment?.status && ['declined', 'timed_out', 'error'].includes(activePayment.status) ? activePayment.status : 'form');
    }
  };

  const handlePayNow = () => pay(tipCents);
  // after a time-out / error: the same amount and tip (the server re-reads the last try first)
  const handleRetry = () => pay(view?.tip_cents ?? activePayment?.tip_cents ?? tipCents);
  const handlePayRemaining = () => pay(0);

  const handleCancel = async () => {
    if (!paymentIdRef.current) return;
    setPhase('cancelling');
    try { track(await cancelPayment(paymentIdRef.current)); } catch { /* the poll reports the reader's verdict */ }
  };

  // "See staff": the order stays as it is (partial / unconfirmed) for staff to finish
  const handleSeeStaff = () => {
    const number = priced?.order_number;
    toast(`Please see staff${number ? ` with order ${number}` : ''}.`, { duration: 6000 });
    startOver();
  };

  /* ═══════════ RENDER ═══════════ */

  if (phase === 'price_error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: '#1e2330' }} data-testid="kiosk-price-error">
        <div className="text-center max-w-md">
          <h2 className="text-3xl font-semibold text-white mb-3">We couldn't get your total</h2>
          <p className="text-lg text-white/60 mb-8">{message}</p>
          <button type="button" onClick={handleBack} className="min-h-[64px] px-10 rounded-2xl bg-[#f5a623] text-[#0f1419] text-lg font-semibold">
            Back to cart
          </button>
        </div>
      </div>
    );
  }

  if (phase !== 'form') {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: '#1e2330' }}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-2xl">
            <PaymentProcessingScreen
              phase={phase}
              view={view}
              amountCents={view ? view.total_cents : (priced?.total_cents || 0) + tipCents}
              chargedCents={Math.max(activePayment?.charged_cents || 0,
                view ? (view.approved_cents || 0) + (view.tip_cents || 0) + (view.fee_cents || 0) : 0)}
              longWait={longWait}
              message={message}
              resetIn={resetIn}
              noCharge={!activePayment?.payment_id || ['declined', 'cancelled'].includes(activePayment?.status)}
              onCancel={handleCancel}
              onRetry={handleRetry}
              onPayRemaining={handlePayRemaining}
              onSeeStaff={handleSeeStaff}
              onStartOver={startOver}
            />
          </div>
        </div>
      </div>
    );
  }

  const unknownReader = !!config?.payment?.reader_status_unknown;
  const reasons = config?.payment?.reasons || [];
  const notReadyReason = reasons.find((r) => !isReaderUnavailable(r));

  return (
    <div className="h-screen flex flex-col" style={{ background: '#1e2330' }} data-testid="kiosk-payment-form">
      <header className="px-6 py-4 flex items-center gap-4 border-b border-white/10" style={{ background: '#262d3d' }}>
        <button
          onClick={handleBack}
          className="w-12 h-12 rounded-xl flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Back to cart"
        >
          <ArrowLeft size={26} />
        </button>
        <h1 className="text-2xl font-bold text-white">Payment</h1>
        {priced?.order_number && <span className="ml-auto text-sm text-white/40">Order {priced.order_number}</span>}
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-6 space-y-6">
          {cart.length > 0 && (
            <OrderSummary
              cart={cart}
              itemCount={getCartCount()}
              isExpanded={orderSummaryExpanded}
              onToggle={() => setOrderSummaryExpanded(!orderSummaryExpanded)}
            />
          )}

          {tipsEnabled && (
            <div className="bg-[#262d3d] border border-white/10 rounded-2xl p-6">
              <TipSelector
                subtotal={tipBase}
                presets={tipPresets}
                selectedPercentage={tipPercentage}
                customAmount={tip}
                onSelect={(pct) => setTipByPercentage(pct, tipBase)}
                onCustomAmountChange={setCustomTip}
              />
            </div>
          )}

          {/* The server's totals */}
          <div className="bg-[#262d3d] border border-white/10 rounded-2xl p-6" data-testid="kiosk-server-totals">
            <PaymentTotals
              subtotal={subtotal}
              discount={(priced?.discount_cents || 0) / 100}
              discountLabel={promoDiscount?.message || ''}
              tax={(priced?.tax_cents || 0) / 100}
              tip={tipCents / 100}
              total={((priced?.total_cents || 0) + tipCents) / 100}
            />
            <p className="text-xs text-white/40 mt-3">A card fee, if the restaurant charges one, is shown on the card reader.</p>
          </div>

          <div className="bg-[#262d3d] border border-white/10 rounded-2xl p-6">
            <PaymentMethodSelector selectedMethod="card" onSelect={() => {}} enableQR={false} enableGiftCard={false} />
            {!cardReady && (
              <div className="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 p-4" data-testid="card-not-ready">
                <p className="text-lg font-semibold text-red-300">{READER_UNAVAILABLE}</p>
                {notReadyReason && <p className="text-sm text-white/50 mt-1">{notReadyReason}</p>}
              </div>
            )}
            {cardReady && unknownReader && (
              <p className="flex items-start gap-2 text-sm text-amber-300/90 mt-3" data-testid="reader-status-unknown">
                <Info size={16} className="mt-0.5 shrink-0" />
                We couldn't confirm the card reader's status. If it doesn't respond, please see staff.
              </p>
            )}
          </div>

        </div>
      </div>

      {/* Pay: always in reach (portrait and landscape) */}
      <footer className="border-t border-white/10 px-6 py-4" style={{ background: '#262d3d' }}>
        <div className="max-w-2xl mx-auto">
          <button
            onClick={handlePayNow}
            disabled={!cardReady || !priced}
            className={`w-full h-20 rounded-2xl text-2xl font-semibold transition-all duration-150 ${cardReady && priced
              ? 'bg-[#f5a623] text-[#0f1419] hover:brightness-110 active:scale-[0.98]'
              : 'bg-white/5 text-white/30 cursor-not-allowed'}`}
            data-testid="pay-now-btn"
          >
            {cardReady ? `Pay $${(((priced?.total_cents || 0) + tipCents) / 100).toFixed(2)}` : 'Card payment unavailable'}
          </button>
        </div>
      </footer>
    </div>
  );
}
